import {
  CustomSelector,
  ECustomSelector,
  SlideDown,
  Typography,
} from '@globalmed/atlas-ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './InstitutionLogo.module.css';
import { InstitutionLogoContent } from '@/components/InstitutionLogoContent';
import { SelectableList } from '@/components/SelectableList';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useInstitutionsStore } from '@/stores/institutions';
import { useUsersStore } from '@/stores/users';
import { useInstitutionChange } from '@/hooks/useInstitutionChange';

export const InstitutionLogo = () => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery('(max-width: 1280px)');
  const { currentInstitution, institutions } = useInstitutionsStore(
    (state) => ({
      currentInstitution: state.currentInstitution,
      institutions: state.institutions,
    }),
  );
  const { availableInstitutionIds, checkIsSuperAdmin } = useUsersStore(
    (state) => ({
      availableInstitutionIds: state.availableInstitutionIds,
      checkIsSuperAdmin: state.checkIsSuperAdmin,
    }),
  );

  const onInstitutionChange = useInstitutionChange();
  const [isOpen, setIsOpen] = useState(false);
  const isSuperAdmin = checkIsSuperAdmin();

  const availableInstitutions = useMemo(() => {
    if (isSuperAdmin) return institutions;
    return institutions.filter((item) =>
      availableInstitutionIds.includes(item.id),
    );
  }, [availableInstitutionIds, institutions, isSuperAdmin]);

  const adapterAvailableInstitutions = useMemo(
    () =>
      availableInstitutions.map((item) => ({
        ...item,
        title: item.whitelabel?.enabled
          ? (item.whitelabel?.organizationName ?? item.name)
          : item.name,
      })),
    [availableInstitutions],
  );

  const handleInstitutionChange = (id: string) => {
    onInstitutionChange(id);
    setIsOpen(false);
  };

  const isCanChangeInstitution =
    adapterAvailableInstitutions.length > 1 || isSuperAdmin;

  const displayName = currentInstitution?.whitelabel?.enabled
    ? (currentInstitution?.whitelabel?.organizationName ??
      currentInstitution?.name)
    : currentInstitution?.name;

  return (
    <div className={styles.container}>
      {isSmallScreen && (
        <InstitutionLogoContent
          whitelabel={currentInstitution?.whitelabel}
          size="sm"
          onClick={
            isSmallScreen && isCanChangeInstitution
              ? () => setIsOpen(true)
              : undefined
          }
        />
      )}
      {!isSmallScreen &&
        (isCanChangeInstitution ? (
          <CustomSelector
            labelProps={{
              label: t('common.select-institution'),
            }}
            placeholder={t('common.example')}
            options={adapterAvailableInstitutions}
            selected={currentInstitution?.id}
            onChange={(id) => handleInstitutionChange(id)}
            sx={{ width: '160px' }}
            type={ECustomSelector.Select}
            enableQuickSearch
          />
        ) : (
          <Typography fontSize="16px">{displayName}</Typography>
        ))}
      <SlideDown
        open={isOpen}
        onOpenChange={setIsOpen}
        title={t('common.select-institution')}
        body={
          <SelectableList
            items={adapterAvailableInstitutions}
            selected={currentInstitution?.id ?? ''}
            aria-label={t('common.select-institution')}
            onChange={(id) => handleInstitutionChange(id)}
          />
        }
      />
    </div>
  );
};
